import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { InputStyle } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";

const enum fields {
    gap = "gap"
}




type Props = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

interface ComponentProps extends Props {
    fields: {
        [fields.gap]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});
interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}
export interface ProductDetailSubCustomContainerStyles {
    container?: GridContainerProps;
    leftColumnGridItemStyle?: GridItemProps;
    rightColumnGridItemStyle?: GridItemProps;
    rightColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    bottomGridItem?: GridItemProps;
    productDetailsActionStyles?: InjectableCss;
    stickyContent?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
}

export const productDetailSubCustomContainerStyles: ProductDetailSubCustomContainerStyles = {
    rightColumnInnerContainer:{
        css: css`
            padding-bottom: 15px;
            min-height: 100vh;
        `
    },
    productDetailsActionStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            > ${TypographyStyle} {
                > ${TypographyStyle} {
                    font-weight: 600;
                }
            }
        `,
    },
    container: {
        gap: 20,
    },
    leftColumnGridItemStyle: {
        width: [12, 12, 8, 8, 8],
        css: css`
            height: fit-content;
        `
    },
    rightColumnGridItemStyle: {
        width: [12, 12, 4, 4, 4],
        css:css`
            position: relative;
        `
    },
    topGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            gap: 10px;
            > div[class^="Wrapper"] {
                > section[class^="sc"] {
                    margin-bottom: 5px;
                }
                > ${FormFieldStyle} {
                    flex-direction: row;
                    margin-bottom: 10px;
                    width: 65%;
                    min-width: 65%;
                    > ${FormInputWrapper} {
                        margin-top: 0px !important;
                        > ${InputStyle} {
                            :focus {
                                border: 2px solid ${getColor("primary")} !important;
                            }
                        }
                    }
                }
            }
        `,
    },
    bottomGridItem: {
        width: 12,
    },
    stickyContent: {
        css: css`
            width: 100%;
        `,
    },
    quoteMessage: {
        text: { weight: "bold", size: "14px" },
        tooltip: {
            iconProps: { size: 14 },
            cssOverrides: {
                tooltipWrapper: css`
                    white-space: normal;
                    z-index: 2;
                `,
                tooltipContainer: css`
                    transform: translateX(-100%);
                `,
            },
        },
    },
};

const styles = productDetailSubCustomContainerStyles;
const quoteStyles = styles.quoteMessage || {};

const ProductDetailSubCustomContainer: FC<ComponentProps> = ({ productContext, id, fields }) => {
    const mainContentRef = React.useRef<HTMLDivElement | null>(null);
       const sidebarRef = React.useRef<HTMLDivElement | null>(null);
       const contentRef = React.useRef<HTMLDivElement | null>(null);
       const topElementRef = React.useRef<HTMLDivElement | null>(null);
       const lastElementRef = React.useRef<HTMLDivElement | null>(null);
       const hasUserInteractedRef = React.useRef(false);
       const [isMobile, setIsMobile] = React.useState<boolean | null>(null);
       const [mainContentHeight, setMainContentHeight] = React.useState(0);
       const [sidebarContentHeight, setSidebarContentHeight] = React.useState(0);
   
       const isCms = ()=>{
           let isInShell = false;
           try {
               isInShell =
                   !!window &&
                   window.parent &&
                   window.parent.location.toString().toLowerCase().indexOf("/contentadmin/") > 0;
           } catch (error) {
           }
           return isInShell;
       }
   
       React.useEffect(() => {
           if (typeof window !== undefined) {
               const handleResize = () => {
                   const mobile = window.innerWidth < 767;
                   setIsMobile(mobile);
               };
   
               handleResize();
   
               window.addEventListener("resize", handleResize);
               return () => window.removeEventListener("resize", handleResize);
           }
       }, []);
   
       React.useEffect(() => {
   
           const measureHeights = () => {
               const mainHeight = mainContentRef.current?.getBoundingClientRect().height ?? 0;
               const sidebarHeight = contentRef.current?.getBoundingClientRect().height ?? 0;
   
               setMainContentHeight(Math.round(mainHeight));
               setSidebarContentHeight(Math.round(sidebarHeight));
           };
   
           measureHeights();
           window.addEventListener("resize", measureHeights);
           window.addEventListener("scroll", measureHeights, { passive: true });

           return () => {
               window.removeEventListener("resize", measureHeights);
               window.removeEventListener("scroll", measureHeights);
           };
       }, []);

       React.useEffect(() => {

           const markUserInteraction = () => {
               hasUserInteractedRef.current = true;
           };

           const removeInteractionListeners = () => {
               window.removeEventListener("wheel", markUserInteraction);
               window.removeEventListener("touchmove", markUserInteraction);
               window.removeEventListener("keydown", markUserInteraction);
           };

           // Early exit: if right content is taller than left, disable sticky entirely
           if (mainContentHeight > 0 && sidebarContentHeight > mainContentHeight) {
               const sidebarContent = contentRef.current;
               if (sidebarContent) {
                   sidebarContent.style.position = "";
                   sidebarContent.style.top = "";
                   sidebarContent.style.bottom = "";
                   sidebarContent.style.transform = "";
                   sidebarContent.style.width = "";
               }
               return;
           }

           // Only proceed if not CMS and not mobile
           if (isCms() || isMobile) {
               return;
           }

           window.addEventListener("wheel", markUserInteraction, { passive: true });
           window.addEventListener("touchmove", markUserInteraction, { passive: true });
           window.addEventListener("keydown", markUserInteraction);

           const handleScroll = () => {
               if (!hasUserInteractedRef.current) {
                   return;
               }

               const sidebar = sidebarRef.current;
               const sidebarContent = contentRef.current;

               if (!sidebar || !sidebarContent) return;

               const scrollTop = window.scrollY;
               const contentHeight = sidebarContent.getBoundingClientRect().height;
               const viewportHeight = window.innerHeight;

               const isTopVisible = topElementRef.current && topElementRef.current.getBoundingClientRect().top >= 0 && topElementRef.current.getBoundingClientRect().top <= window.innerHeight;
               const sidebarTop = sidebar.getBoundingClientRect().top + window.pageYOffset;

               if(isTopVisible){
                   sidebarContent.style.position = "relative";
                   sidebarContent.style.top = "0px";
                   sidebarContent.style.bottom = "auto";
                   sidebarContent.style.transform = "none";
                   sidebarContent.style.width = `${sidebar.offsetWidth - fields.gap}px`;
                   return;
               }
               if (
                   scrollTop >=
                   contentHeight - viewportHeight + sidebarTop
               ) {
                   if (
                       lastElementRef.current &&
                       lastElementRef.current.getBoundingClientRect().top <= window.innerHeight
                   ) {
                       sidebarContent.style.position = "absolute";
                       sidebarContent.style.top = "auto";
                       sidebarContent.style.bottom = "0px";
                       sidebarContent.style.transform = "none";
                       sidebarContent.style.width = `${sidebar.offsetWidth - fields.gap}px`;
                   } else {
                       sidebarContent.style.position = "fixed";
                       sidebarContent.style.top = "0";
                       sidebarContent.style.bottom = "auto";
                       sidebarContent.style.transform = `translateY(-${contentHeight - viewportHeight}px)`;
                       sidebarContent.style.width = `${sidebar.offsetWidth - fields.gap}px`;
                   }
               } else {
                   // Reset
                   sidebarContent.style.position = "";
                   sidebarContent.style.top = "";
                   sidebarContent.style.bottom = "";
                   sidebarContent.style.transform = "";
                   sidebarContent.style.width = "";
               }
           };
           window.addEventListener("scroll", handleScroll);

           return () => {
               removeInteractionListeners();
               window.removeEventListener("scroll", handleScroll);
           };
       }, [isMobile, mainContentHeight, sidebarContentHeight, fields]);

    const productListingPrice = productContext.productInfo.pricing?.unitRegularPrice;
    const productQuoteRequired = productContext.product.quoteRequired;
    const productActionsSiteMessage = siteMessage("ProductActions_SiteMessage");
    return (
        <GridContainer {...styles.container}>
             {/* Top Marker */}
            <GridItem width={12} style={{padding: "0px"}} ref={topElementRef}></GridItem>
            <GridItem {...styles.leftColumnGridItemStyle}  ref={mainContentRef}>
                <StyledWrapper {...styles.stickyContent} >
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.rightColumnGridItemStyle} ref={sidebarRef}>
                <div ref={contentRef}>
                    <GridContainer {...styles.rightColumnInnerContainer}>
                        <GridItem {...styles.topGridItem}>
                            {productListingPrice! < 1 && productQuoteRequired === false ? (
                                <>
                                    <StyledWrapper {...styles.productDetailsActionStyles}>
                                        <StyledWrapper style={{display:"flex", alignItems:"center", gap:"5px"}}>
                                            {translate("Price:")}
                                                <StyledWrapper  style={{display:"flex", alignItems:"center", gap:"1px"}}>
                                                    <Typography {...quoteStyles.text}>
                                                        Requires Quote
                                                    </Typography>
                                                    <Tooltip
                                                        {...quoteStyles.tooltip}
                                                        text={siteMessage("Rfq_TooltipMessage").toString()}
                                                    />
                                                </StyledWrapper>
                                        </StyledWrapper>
                                        <Typography>{productActionsSiteMessage}</Typography>
                                    </StyledWrapper>
                                    <Zone contentId={id} zoneName="Content03" />
                                </>
                            ) : (
                                <Zone contentId={id} zoneName="Content01" />
                            )}
                        </GridItem>
                        <GridItem {...styles.bottomGridItem}>
                            <Zone contentId={id} zoneName="Content02" />
                        </GridItem>
                    </GridContainer>
                </div>
            </GridItem>
           <GridItem width={12} style={{padding: "0px"}} ref={lastElementRef}></GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailSubCustomContainer)),
    definition: {
        group: "Common",
        displayName: "Product Detail Container",
        fieldDefinitions: [
            {
                name: fields.gap,
                displayName: "Gap Between Column",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
